import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { ContainerCard } from "../../components/ui/cards";
import { DOCUMENT_UPLOAD_REPORT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

/**
 * Document Upload Report — one row per employee, one Yes/No column per document.
 *
 * The document columns are not known at build time: the statutory ones plus every
 * row of the Document Type master. So the page asks the API for the column list
 * first (?meta=true) and only mounts the table once it has them — that way
 * Reactable fetches the rows exactly once, with the right columns already in place.
 */
const UPLOAD_STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "pending", label: "Missing documents" },
    { key: "complete", label: "All collected" },
];

const DocumentUploadReport = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();

    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [documentColumns, setDocumentColumns] = useState(null);
    const [loadingColumns, setLoadingColumns] = useState(true);
    const [uploadStatus, setUploadStatus] = useState("all");

    const statusFilter = useMemo(() => ({ upload_status: uploadStatus }), [uploadStatus]);

    useEffect(() => {
        const controller = new AbortController();

        const loadColumns = async () => {
            setLoadingColumns(true);
            const response = await fetch(DOCUMENT_UPLOAD_REPORT, { meta: true }, null, controller.signal);
            if (response?.status === "cancelled") return;

            if (response?.status === "success") {
                setDocumentColumns(response.data?.documents || []);
            } else {
                setDocumentColumns([]);
                ToastMsg("error", response?.message || "Failed to load document columns");
            }
            setLoadingColumns(false);
        };

        loadColumns();
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const EmployeeCell = ({ row }) => (
        <div>
            <div className="font-medium text-gray-900 dark:text-white">{row?.employee_name || "-"}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{row?.employee_code || "-"}</div>
        </div>
    );

    const YesNoCell = ({ value }) => (
        <span className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
            value === "Yes"
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
        )}>
            {value === "Yes" ? "Yes" : "No"}
        </span>
    );

    const columns = useMemo(() => [
        { accessor: "employee_name", Header: "Employee", fixed: true, filterable: true, maxWidth: "260px", Cell: EmployeeCell },
        { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
        { accessor: "designation", Header: "Designation", filterable: true, Cell: ({ value }) => value ?? "-" },
        ...(documentColumns || []).map((doc) => ({
            accessor: doc.key,
            Header: doc.label,
            sortable: true,
            Cell: YesNoCell,
        })),
        {
            accessor: "uploaded_count",
            Header: "Uploaded",
            Cell: ({ value }) => (
                <span className="font-medium text-gray-700 dark:text-gray-300">{value ?? "-"}</span>
            ),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [documentColumns]);

    return (
        <ContainerCard className="font-sans">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4 shrink-0">
                <span className="flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => navigate("/reports")} className="cursor-pointer" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Document Upload Report ({totalRecords})
                    </h1>
                </span>

                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {UPLOAD_STATUS_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setUploadStatus(tab.key)}
                            className={cn(
                                "px-4 py-1.5 text-sm font-medium transition-colors",
                                uploadStatus === tab.key
                                    ? "bg-sidebar-primary text-white"
                                    : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                "Yes" means the document file is on record for that employee. Columns come from the
                statutory documents plus every entry in the Document Type master, so adding a document
                type adds a column here.
            </p>

            {loadingColumns ? (
                <div className="h-[calc(100vh-310px)] flex items-center justify-center rounded-md">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                </div>
            ) : (
                <Reactable
                    columns={columns}
                    data={data}
                    setData={setData}
                    setTotalCount={setTotalRecords}
                    url={DOCUMENT_UPLOAD_REPORT}
                    serverSide={true}
                    serverTrigger={uploadStatus}
                    filter={statusFilter}
                    printFilter={statusFilter}
                    rowSize="sm"
                    isCSVRequired={true}
                    isPDFRequired={true}
                    isFromToDateRequired={true}
                />
            )}
        </ContainerCard>
    );
}

export default DocumentUploadReport;
